<template>
  <div>
    <v-skeleton-loader type="table" :loading="loading" width="90%" class="mx-auto">
      <v-container>
        <v-card flat>
          <!-- Filter Section -->
          <v-card-text v-if="filters.length > 0">
            <v-row dense class="d-flex justify-center">
              <v-col v-for="filter in filters" :key="filter.model" cols="12" sm="6" md="4">
                <v-text-field
                  v-if="!filter.type || filter.type === 'text' || filter.type === 'number'"
                  v-model="filterValues[filter.model]"
                  :label="filter.label"
                  :type="filter.type || 'text'"
                  clearable
                  @update:model-value="applyFilters"
                  :prepend-inner-icon="filter.icon"
                />
                <v-select
                  v-if="filter.type === 'select'"
                  v-model="filterValues[filter.model]"
                  :items="filter.items"
                  :label="filter.label"
                  clearable
                  @update:model-value="applyFilters"
                  :prepend-inner-icon="filter.icon"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Table Section -->
          <v-table>
            <thead>
              <tr>
                <th v-for="col in columns" :key="col.key" class="font-weight-bold">{{ col.title }}</th>
                <th v-if="actions.length > 0" class="font-weight-bold text-center">Ações</th>
              </tr>
            </thead>
            <tbody class="zebra-table">
              <tr v-for="item in paginatedItems" :key="item[itemKey]">
                <td v-for="col in columns" :key="col.key">
                  <v-chip v-if="col.type === 'chip'" :color="col.color(item)" dark>{{ col.format ? col.format(item[col.key]) : item[col.key] }}</v-chip>
                  <span v-else>{{ col.format ? col.format(item[col.key]) : item[col.key] }}</span>
                </td>
                <td v-if="actions.length > 0" class="text-center">
                    <v-tooltip v-for="action in actions" :key="action.tooltip" location="top">
                        <template #activator="{ props }">
                            <span v-bind="props">
                                <v-btn icon :color="action.color || 'primary'" @click="action.handler(item)" variant="plain">
                                    <v-icon>{{ action.icon }}</v-icon>
                                </v-btn>
                            </span>
                        </template>
                        <span>{{ action.tooltip }}</span>
                    </v-tooltip>
                </td>
              </tr>
              <tr v-if="paginatedItems.length === 0">
                <td :colspan="columns.length + (actions.length > 0 ? 1 : 0)" class="text-center text-grey">
                  Nenhum item encontrado.
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- Pagination Section -->
          <v-container max-width="500">
            <v-pagination v-model="currentPage" :length="totalPages" class="mt-4"></v-pagination>
          </v-container>
        </v-card>
      </v-container>
    </v-skeleton-loader>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import api from '../../services/api';

export default {
  name: 'TabelaDeDados',
  props: {
    apiRoute: { type: String, required: true },
    itemKey: { type: String, required: true },
    columns: { type: Array, required: true }, // { key, title, format?, type?, color? }
    filters: { type: Array, default: () => [] }, // { model, label, type?, icon?, items? }
    actions: { type: Array, default: () => [] }, // { icon, color?, tooltip, handler }
    sortBy: { type: String, default: null },
    sortDesc: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      filteredItems: [],
      loading: false,
      currentPage: 1,
      itemsPerPage: 10,
      filterValues: {},
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.itemsPerPage);
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      
      const ordered = [...this.filteredItems].sort((a, b) => {
        const sortKey = this.sortBy || this.columns[0]?.key;
        if (!sortKey) return 0;

        let valA = a[sortKey];
        let valB = b[sortKey];

        if (String(valA).match(/^\d{4}-\d{2}-\d{2}/)) {
            valA = new Date(valA);
            valB = new Date(valB);
        }

        if (valA < valB) return this.sortDesc ? 1 : -1;
        if (valA > valB) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return ordered.slice(start, end);
    },
  },
  mounted() {
    this.initializeFilters();
    this.fetchData();
  },
  methods: {
    ...mapActions(['triggerFeedback']),
    initializeFilters() {
        const initialFilters = {};
        this.filters.forEach(f => {
            initialFilters[f.model] = null;
        });
        this.filterValues = initialFilters;
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get(this.apiRoute);
        this.items = response.data;
        this.applyFilters();
      } catch (error) {
        this.triggerFeedback({
          message: `Erro ao buscar dados de ${this.apiRoute}.`,
          color: 'error',
          icon: 'mdi-alert-circle',
        });
      }
      this.loading = false;
    },
    applyFilters() {
      let tempList = [...this.items];
      for (const key in this.filterValues) {
        const filterValue = this.filterValues[key];
        if (filterValue !== null && filterValue !== '' && filterValue !== undefined) {
          const filterDef = this.filters.find(f => f.model === key);
          const itemKey = filterDef?.itemKey || key;

          tempList = tempList.filter(item => {
            if (item[itemKey] === undefined || item[itemKey] === null) return false;
            if (typeof filterValue === 'boolean') {
                return item[itemKey] === filterValue;
            }
            return String(item[itemKey]).toLowerCase().includes(String(filterValue).toLowerCase());
          });
        }
      }
      this.filteredItems = tempList;
      this.currentPage = 1;
    },
    recarregar() {
      this.fetchData();
    }
  },
};
</script>

<style scoped>
.zebra-row {
  background-color: #f5f5f5;
}
.zebra-table tr:hover {
  background-color: #e0e0e0;
}
thead {
  background-color: #e3f0ff; 
  font-size: 20px;
  color: #1976d2;            
}
tbody {
  font-size: 18px;
  background-color: #fafdff; 
}
td {
  color: #222;
  background-color: inherit;
  border-bottom: 1px solid #e3e7ed;
}
tr.zebra-row td {
  background-color: #f5faff;
}
tr:hover td {
  background-color: #eaf4ff !important;
}
th, td {
  padding: 12px 10px;
}
.v-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(25, 118, 210, 0.06);
}
</style>